import React from 'react';
import { Typography, Tag, Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import AuthLayout from '../../layouts/AuthLayout';
import sthenusLogo from '../../assets/sthenus-logo.png';
import squiggleLogo from '../../assets/squiggle.svg';
import DeleteAccountForm from './DeleteAccountForm';
import {
  TOPIC_CHIPS,
  HERO_PANEL_ITEMS,
  SIDEBAR_ITEMS,
  PROCESS_STEPS,
  RETENTION_ITEMS,
} from './deleteAccountContent';
import '../../styles/support.css';
import '../../styles/delete-account.css';

const { Title, Paragraph, Text } = Typography;

const scrollToSection = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

export default function DeleteAccountPage() {
  const navigate = useNavigate();

  return (
    <AuthLayout>
      <div className="support-page">
        <div className="delete-account-hero">
          <div className="delete-account-hero__content">
            <span className="delete-account-hero__label">
              <span className="delete-account-hero__label-dot" aria-hidden="true" />
              Eliminación de cuenta
            </span>
            <Title level={2} className="delete-account-hero__title">
              Solicita la eliminación de tu cuenta Sthenus.
            </Title>
            <Paragraph className="delete-account-hero__subtitle">
              Usa este formulario para validar tu identidad y eliminar tu cuenta junto con los
              datos personales asociados, conforme a la política de privacidad y las obligaciones
              legales aplicables.
            </Paragraph>
          </div>

          <aside className="delete-account-hero__panel">
            <div className="delete-account-hero__brand">
              <img
                src={squiggleLogo}
                alt="Sthenus"
                className="delete-account-hero__brand-icon"
              />
              <span className="delete-account-hero__brand-name">Sthenus</span>
            </div>

            <ul className="delete-account-hero__info">
              {HERO_PANEL_ITEMS.map((item) => (
                <li key={item.title} className="delete-account-hero__info-item">
                  <strong>{item.title}</strong>
                  {item.description === 'privacy' ? (
                    <a
                      href="/privacy-policy"
                      className="delete-account-hero__link"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/privacy-policy');
                      }}
                    >
                      Ver política de privacidad
                    </a>
                  ) : (
                    item.description
                  )}
                </li>
              ))}
            </ul>
          </aside>
        </div>

        <div className="support-layout">
          <aside className="support-sidebar">
            <img src={sthenusLogo} alt="Sthenus" className="support-sidebar__logo" />

            <ul className="support-sidebar__list">
              {SIDEBAR_ITEMS.map((item) => (
                <li key={item.title} className="support-sidebar__item">
                  <strong>{item.title}</strong>
                  {item.description === 'privacy' ? (
                    <a
                      href="/privacy-policy"
                      className="support-sidebar__link"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/privacy-policy');
                      }}
                    >
                      Ver política de privacidad
                    </a>
                  ) : item.title === 'Soporte' ? (
                    <>
                      {item.description}{' '}
                      <a
                        href="/support"
                        className="support-sidebar__link"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/support');
                        }}
                      >
                        Centro de ayuda
                      </a>
                    </>
                  ) : (
                    item.description
                  )}
                </li>
              ))}
            </ul>

            <Button
              type="default"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/login')}
              block
            >
              Regresar al inicio
            </Button>
          </aside>

          <main>
            <div className="support-chips">
              {TOPIC_CHIPS.map((chip) => (
                <Tag key={chip} className="support-chip">
                  {chip}
                </Tag>
              ))}
            </div>

            <nav className="support-nav">
              <a
                href="#proceso"
                className="support-nav__link"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('proceso');
                }}
              >
                Qué sucede
              </a>
              <a
                href="#formulario"
                className="support-nav__link"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('formulario');
                }}
              >
                Formulario
              </a>
              <a
                href="#conservados"
                className="support-nav__link"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('conservados');
                }}
              >
                Datos conservados
              </a>
              <a
                href="/support"
                className="support-nav__link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/support');
                }}
              >
                Contactar soporte
              </a>
              <a
                href="/login"
                className="support-nav__link support-nav__link--muted"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/login');
                }}
              >
                Regresar al inicio
              </a>
              <a
                href="/privacy-policy"
                className="support-nav__link support-nav__link--muted"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/privacy-policy');
                }}
              >
                Política de privacidad
              </a>
              <a
                href="/terms-and-conditions"
                className="support-nav__link support-nav__link--muted"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/terms-and-conditions');
                }}
              >
                Términos y condiciones
              </a>
            </nav>

            <section id="proceso" className="support-section">
              <Title level={3} className="support-section__title">
                Qué sucede al eliminar tu cuenta
              </Title>
              <Text className="support-section__description">
                Envía tu correo electrónico para iniciar el proceso de eliminación. Recibirás un
                enlace de confirmación que protege tu cuenta contra solicitudes no autorizadas.
              </Text>
              <ol className="delete-account-steps">
                {PROCESS_STEPS.map((step) => (
                  <li key={step} className="delete-account-steps__item">
                    {step}
                  </li>
                ))}
              </ol>
              <Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
                Si requieres que se realice una eliminación directa, puedes hacerla directamente
                desde la aplicación móvil de Sthenus. En cuanto se complete la solicitud y la
                confirmación desde el celular, se cerrará tu sesión automáticamente.
              </Paragraph>
            </section>

            <section id="formulario" className="support-section">
              <Title level={3} className="support-section__title">
                Formulario de eliminación
              </Title>
              <Text className="support-section__description">
                Confirma tu identidad para solicitar la eliminación de cuenta. Esta acción puede
                impedir el acceso posterior a rutinas, progreso y funciones asociadas al usuario.
              </Text>
              <DeleteAccountForm />
            </section>

            <section id="conservados" className="support-section">
              <Title level={3} className="support-section__title">
                Datos que podrían conservarse
              </Title>
              <Text className="support-section__description">
                La eliminación de cuenta no siempre implica borrar todos los registros de forma
                inmediata. Sthenus puede conservar información mínima cuando sea necesaria para
                cumplir obligaciones legales, fiscales, contables, administrativas, seguridad,
                prevención de fraude o defensa de reclamaciones.
              </Text>
              <div className="delete-account-retention">
                <ul className="delete-account-retention__list">
                  {RETENTION_ITEMS.map((item) => (
                    <li key={item.title} className="delete-account-retention__item">
                      <strong>{item.title}:</strong> {item.description}
                    </li>
                  ))}
                </ul>
              </div>
              <Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
                Para más detalle, consulta la{' '}
                <a
                  href="/privacy-policy"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/privacy-policy');
                  }}
                >
                  Política de Privacidad
                </a>{' '}
                y los{' '}
                <a
                  href="/terms-and-conditions"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/terms-and-conditions');
                  }}
                >
                  Términos y Condiciones
                </a>{' '}
                de Sthenus.
              </Paragraph>
            </section>

            <footer className="support-footer">
              <div className="support-footer__links">
                <a
                  href="/login"
                  className="support-footer__link"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/login');
                  }}
                >
                  Regresar al inicio
                </a>
                <a
                  href="/support"
                  className="support-footer__link"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/support');
                  }}
                >
                  Centro de ayuda
                </a>
                <a
                  href="/privacy-policy"
                  className="support-footer__link"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/privacy-policy');
                  }}
                >
                  Política de privacidad
                </a>
                <a
                  href="/terms-and-conditions"
                  className="support-footer__link"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/terms-and-conditions');
                  }}
                >
                  Términos y condiciones
                </a>
              </div>
              <Text type="secondary">
                Sthenus | Solicitud de eliminación de cuenta y datos personales.
              </Text>
            </footer>
          </main>
        </div>
      </div>
    </AuthLayout>
  );
}
